/**
 * Drives the field-mapping screen for any connected CRM: what the advisor's CRM exposes, what
 * Waterlily has matched it to, and what the advisor decides.
 *
 * Discovery runs on every visit rather than reading a cache, because a CRM's fields and an
 * advisor's permissions both change without telling us, and a mapping built on a stale picture is
 * what makes a later read fail. The selection the advisor is editing is kept as a sparse draft
 * layered over the saved map, so no effect is needed to seed state from the query — a row falls
 * back to what the server holds until the advisor touches it.
 *
 * Saving submits a decision for every row that changed or is still only a suggestion: a chosen
 * field is confirmed, and clearing a field that was previously matched is a rejection, which is what
 * stops the next discovery run from proposing the same wrong match again. Rows already confirmed and
 * untouched are not sent again. One of them may point at a field the CRM has since dropped, and
 * resending it would have the server refuse the whole save.
 *
 * The save's answer is merged into the list on screen straight away, so a row shows its new status
 * without waiting for the fresh read of the CRM that follows.
 *
 * Each row also carries what the matcher made of it: the candidates it could be, and the section of
 * the screen it belongs in (see `features/crm-field-map/lib/field-map-groups`).
 *
 * A confirmed map queues a new read of the book, so a save refreshes the sync status along with the
 * field list; otherwise the page goes on saying the book was last read before the change. Both are
 * refreshed after a failed save too, unless the server refused it: a save the client stopped waiting
 * for can still go through. The advisor's edits are cleared only when the save is known to have worked.
 *
 * A mapping also says what the value means, and the server holds the advisor to it. A number cannot
 * be confirmed without its scale — `Net_Worth__c = 250` may be dollars, thousands or millions — so a
 * number row with no scale blocks the save rather than being refused by the server. A yes/no mapped
 * to something that is not a checkbox (a date of death, a status picklist) needs a rule saying what
 * means yes; without one it is saved but not read, and the row says so. Changing a row's field
 * clears its meaning, because a scale or a rule chosen for one field says nothing about another.
 *
 * The Waterlily stage is not offered at all: it comes from Waterlily's own records. Fields that can
 * be filled from related records — family links, meetings, products — are still offered, since an
 * advisor may keep them on the contact, and a mapping wins over the related read.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiClientError } from '@client/api/apiClient';
import { prospectingApi, queryKeys } from '@client/api/integrations';
import type { Prospecting } from '@client/api/types';
import { fieldMapCopy } from '@client/features/crm-field-map/lib/field-map-copy';
import {
  UNMAPPED,
  groupOf,
  groupRows,
  hasYesRule,
  summarise,
  type FieldMapGroupKey,
} from '@client/features/crm-field-map/lib/field-map-groups';
import { applySavedFieldMap } from '@client/features/crm-field-map/lib/saved-field-map';

export { UNMAPPED };

/** A refusal (4xx) saved nothing. A timeout or a server error may still have saved. */
function mayHaveSaved(error: unknown): boolean {
  return !(error instanceof ApiClientError) || error.status >= 500;
}

export interface FieldMapRow {
  canonicalField: string;
  label: string;
  kind: Prospecting.CanonicalFieldKind;
  /** The field currently chosen in the UI, or UNMAPPED. */
  selected: string;
  /** What the server holds today, or UNMAPPED if it holds nothing. */
  saved: string;
  status: Prospecting.FieldMapStatus | null;
  matchedOn: string | null;
  /** The chosen CRM field as discovery described it, for asking what its values mean. */
  selectedField: Prospecting.DiscoveredField | null;
  /** What the chosen field's values mean, as drafted or as saved. */
  semantics: Prospecting.FieldSemantics | null;
  /** A number with no scale: the save is blocked until the advisor gives one. */
  needsScale: boolean;
  /** A yes/no mapped to a field that is not one, so a rule decides what means yes. */
  needsBooleanRule: boolean;
  /** Filled from related records, or from Waterlily, when nothing is mapped. */
  isDerived: boolean;
  /** True when this row would be part of the next save. */
  isPending: boolean;
  role: Prospecting.CanonicalFieldRole | null;
  /** A yes/no read from a field that is not one, with no rule yet: saved, but not read. */
  missingYesRule: boolean;
  /** The saved CRM field is no longer in the CRM, so nothing is read through this row. */
  sourceMissing: boolean;
  /** What this field could be in the CRM, best first. Empty when nothing plausible was found. */
  candidates: Prospecting.FieldMatchCandidate[];
  /** CRM field types that do not usually hold this field, for keeping the picker's list short. */
  unsuitableTypes: string[];
  /** Which section of the screen the row sits in, from what the server holds. */
  group: FieldMapGroupKey;
}

export function useCrmFieldMap(provider: Prospecting.Provider, objectName = 'Contact') {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [semanticsDraft, setSemanticsDraft] = useState<Record<string, Prospecting.FieldSemantics>>(
    {},
  );

  const query = useQuery({
    queryKey: queryKeys.prospecting.schema(provider, objectName),
    queryFn: () => prospectingApi.schema.discover(provider, objectName),
  });

  const saved = useMemo(() => {
    const entries = query.data?.fieldMap ?? [];
    return new Map(entries.map(entry => [entry.canonicalField, entry]));
  }, [query.data]);

  const rows = useMemo<FieldMapRow[]>(() => {
    const canonicalFields = (query.data?.canonicalFields ?? []).filter(
      field => field.source !== 'waterlily',
    );
    const fieldsByName = new Map((query.data?.fields ?? []).map(field => [field.name, field]));
    const coverageState = new Map(
      (query.data?.coverage?.fields ?? []).map(field => [field.canonicalField, field.state]),
    );

    return canonicalFields.map(({ key, label, kind, role }) => {
      const entry = saved.get(key);
      const savedField = entry && entry.status !== 'rejected' ? entry.sourceField : UNMAPPED;
      const savedType =
        savedField === UNMAPPED ? null : (fieldsByName.get(savedField)?.type ?? entry?.sourceType ?? null);
      const candidates = query.data?.candidates?.[key] ?? [];
      const isDerived = coverageState.get(key) === 'derived';
      const sourceMissing = entry?.status === 'confirmed' && !fieldsByName.has(entry.sourceField);
      const selected = draft[key] ?? savedField;
      const selectedField = selected === UNMAPPED ? null : (fieldsByName.get(selected) ?? null);
      const semantics =
        semanticsDraft[key] ?? (selected === savedField ? (entry?.semantics ?? null) : null);
      const isMapped = selected !== UNMAPPED;

      return {
        canonicalField: key,
        label,
        kind,
        selected,
        saved: savedField,
        status: entry?.status ?? null,
        matchedOn: entry?.matchedOn ?? null,
        selectedField,
        semantics,
        needsScale: isMapped && kind === 'number' && !semantics?.scale,
        needsBooleanRule: isMapped && kind === 'boolean' && selectedField?.type !== 'boolean',
        isDerived,
        isPending:
          selected !== savedField ||
          key in semanticsDraft ||
          (isMapped && entry?.status === 'suggested'),
        role: role ?? null,
        missingYesRule:
          isMapped && kind === 'boolean' && selectedField?.type !== 'boolean' && !hasYesRule(semantics),
        sourceMissing,
        candidates,
        unsuitableTypes: query.data?.unsuitableTypes?.[key] ?? [],
        group: groupOf({
          status: entry?.status ?? null,
          savedNeedsMeaning:
            savedField !== UNMAPPED &&
            ((kind === 'number' && !entry?.semantics?.scale) ||
              (kind === 'boolean' && savedType !== 'boolean' && !hasYesRule(entry?.semantics))),
          sourceMissing,
          isDerived,
          hasCandidates: candidates.length > 0,
        }),
      };
    });
  }, [query.data, saved, draft, semanticsDraft]);

  const decisions = useMemo<Prospecting.FieldMapDecision[]>(
    () =>
      rows.flatMap((row): Prospecting.FieldMapDecision[] => {
        if (!row.isPending) return [];
        if (row.selected !== UNMAPPED) {
          return [
            {
              canonicalField: row.canonicalField,
              sourceField: row.selected,
              status: 'confirmed',
              ...(row.semantics ? { semantics: row.semantics } : {}),
            },
          ];
        }
        const previous = saved.get(row.canonicalField);
        if (previous && previous.status !== 'rejected') {
          return [
            {
              canonicalField: row.canonicalField,
              sourceField: previous.sourceField,
              status: 'rejected',
            },
          ];
        }
        return [];
      }),
    [rows, saved],
  );

  const refreshAfterSave = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.prospecting.schema(provider, objectName),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.prospecting.sync(provider, objectName),
    });
  };

  const mutation = useMutation({
    mutationFn: () => prospectingApi.schema.confirmFieldMap(provider, objectName, decisions),
    onSuccess: response => {
      queryClient.setQueryData<Prospecting.SchemaDiscoveryResponse>(
        queryKeys.prospecting.schema(provider, objectName),
        current => applySavedFieldMap(current, response),
      );
      setDraft({});
      setSemanticsDraft({});
      refreshAfterSave();
      toast.success(fieldMapCopy.actions.saved);
    },
    onError: error => {
      if (mayHaveSaved(error)) refreshAfterSave();
    },
  });

  const missingScaleCount = rows.filter(row => row.needsScale).length;

  return {
    provider,
    objectName,
    /** Whether this CRM's discovery reflects what the user may read, or only what exists. */
    capabilities: query.data?.capabilities,
    rows,
    groups: groupRows(rows),
    summary: summarise(rows),
    fields: query.data?.fields ?? [],
    readableFieldCount: query.data?.readableFieldCount ?? 0,
    discoveredAt: query.data?.discoveredAt,
    /** False until a field list has arrived, so a failed first read is not shown as an empty one. */
    hasLoaded: query.data !== undefined,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    select: (canonicalField: string, sourceField: string) => {
      setDraft(current => ({ ...current, [canonicalField]: sourceField }));
      setSemanticsDraft(current => {
        const next = { ...current };
        delete next[canonicalField];
        return next;
      });
    },
    setSemantics: (canonicalField: string, semantics: Prospecting.FieldSemantics) =>
      setSemanticsDraft(current => ({ ...current, [canonicalField]: semantics })),
    reset: () => {
      setDraft({});
      setSemanticsDraft({});
    },
    pendingCount: rows.filter(row => row.isPending).length,
    /** Pending rows that clear a mapping the server holds. */
    removalCount: rows.filter(row => row.isPending && row.selected === UNMAPPED).length,
    /** Numbers still waiting for a scale. The save is blocked while this is above zero. */
    missingScaleCount,
    hasUnsavedEdits: Object.keys(draft).length > 0 || Object.keys(semanticsDraft).length > 0,
    save: () => mutation.mutate(),
    isSaving: mutation.isPending,
    saveError: mutation.error,
    savedAt: mutation.isSuccess ? mutation.submittedAt : undefined,
  };
}
